"use client";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import FormatDate from "@/utils/FormatDate";
import BlogCard_Thumbnail from "@/assets/image/BlogCard_Thumbnail.jpg";
import { getReadingTime } from "@/helpers/readingTime";

// Import Swiper styles
// import "swiper/css";
// import "swiper/css/pagination";

export default function RecentBlogs({ blogs, article }) {
  if (!blogs || !article) {
    return null;
  }

  const recentBlogs = blogs
    .filter((blog) => blog.id !== article.id && blog.slug !== article.slug)
    .slice(0, 10);

  if (recentBlogs.length === 0) {
    return null;
  }

  const readingTime = (blog) =>
    getReadingTime(
      (blog.blocks || [])
        .map((block) => block.Editor || block.body || "")
        .join(" "),
    );

  return (
    <div className="py-[16px] lg:border lg:border-[#D7DFE6] lg:px-[28px] lg:py-[16px]">
      
      {/* Exactly ONE H2 Heading */}
      <h2 className="text-2xl leading-[40px] tracking-[0] align-middle mb-[12px] lg:mb-[10px] text-[#202124]">
        Recent Blogs
      </h2>

      {/* ── MOBILE VIEW: Swiper Carousel ── */}
      <div className="block lg:hidden">
        <Swiper
          modules={[Pagination]}
          spaceBetween={20}
          slidesPerView={1.2}
          className="recent-blogs-swiper"
        >
          {recentBlogs.map((blog) => (
            <SwiperSlide key={blog.id}>
              <BlogCard 
                blog={blog} 
                isDesktopView={false} 
                readingTimeText={readingTime(blog)} 
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* ── DESKTOP VIEW: Stacked Row List ── */}
      <div className="hidden lg:flex lg:flex-col lg:gap-[18px] lg:mt-[10px] max-h-[500px] overflow-y-scroll :pr-[10px]">
        {recentBlogs.map((blog, index, arr) => (
          <div key={blog.id} className="flex flex-col gap-[18px]">
            <BlogCard 
              blog={blog} 
              isDesktopView={true} 
              readingTimeText={readingTime(blog)} 
            />
            {index !== arr.length - 1 && (
              <hr className="border border-[#D7DFE6]" />
            )}
          </div>
        ))}
      </div>

    </div>
  );
}

// ── Reusable Card Component ──
function BlogCard({ blog, isDesktopView, readingTimeText }) {
  const imageSrc = blog.cover?.url || BlogCard_Thumbnail;
  const imageAlt = blog.cover?.alternativeText || blog.title;

  return (
    <Link href={`/blog/${blog.slug}`} className="block group w-full">
      
      {/* Layout Switcher:
        Mobile (isDesktopView = false): Stays stacked (flex-col)
        Desktop (isDesktopView = true): Goes side-by-side (flex-row)
      */}
      <div className={`flex ${isDesktopView ? "flex-row gap-[18px]" : "flex-col gap-0"}`}>
        
        {/* Image Frame */}
        <div 
          className={`relative ${isDesktopView ? "h-[110px]" : "mb-[12px]"}`}
          style={isDesktopView ? { width: "45%" } : undefined}
        >
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={isDesktopView ? 176 : 320}
            height={isDesktopView ? 110 : 200}
            className={isDesktopView ? "object-cover" : "w-full"}
          />
        </div>

        {/* Content Bundle */}
        <div 
          className={`flex flex-col ${isDesktopView ? "gap-[12px]" : "gap-0"}`}
          style={isDesktopView ? { width: "55%" } : undefined}
        >
          {/* Category Tag */}
          <span
            className={`inline-block tracking-[0] leading-[100%] text-[#EB9100] bg-[#FFF0D7] py-[3px] px-[8px] w-fit ${
              isDesktopView ? "text-[14px]" : "font-medium text-sm mb-[10px]"
            }`}
            style={{ fontFamily: isDesktopView ? "saans-regular" : "saans-medium" }}
          >
            {blog.category?.name || "MSU"}
          </span>

          {/* Title Header */}
          <h4
            className={`text-[16px] leading-[24px] text-[#202124] group-hover:text-[#005D82] transition-colors ${
              isDesktopView ? "line-clamp-3 font-[600px] tracking-normal" : "tracking-[0] mb-[8px]"
            }`}
            style={{ fontFamily: "saans-medium" }}
          >
            {blog.title}
          </h4>
        </div>
      </div>

      {/* Metadata Row */}
      <div className={`flex items-center gap-[8px] ${isDesktopView ? "mt-[15px]" : ""}`}>
        {isDesktopView ? (
          // Desktop Meta Elements (p tags)
          <>
            <p className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]" style={{ fontFamily: "saans-medium" }}>
              {blog.author?.name || "Anonymous"}
            </p>
            <span className="w-[3px] h-[3px] bg-[#63778B] rounded-full"></span>
            <p className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]" style={{ fontFamily: "saans-medium" }}>
              {FormatDate(blog.updatedAt)}
            </p>
            <span className="w-[3px] h-[3px] bg-[#63778B] rounded-full"></span>
            <p className="text-[12px] leading-[14.4px] tracking-normal text-[#63778B]" style={{ fontFamily: "saans-medium" }}>
              {readingTimeText}
            </p>
          </>
        ) : (
          // Mobile Meta Elements (span tags)
          <>
            <span className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]" style={{ fontFamily: "saans-regular" }}>
              {blog.author?.name || "Anonymous"}
            </span>
            <span className="w-[4px] h-[4px] bg-[#D7DFE6] rounded-full"></span>
            <span className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]" style={{ fontFamily: "saans-regular" }}>
              {FormatDate(blog.updatedAt)}
            </span>
            <span className="w-[4px] h-[4px] bg-[#D7DFE6] rounded-full"></span>
            <span className="text-[14px] leading-[100%] tracking-[0] text-[#63778B]" style={{ fontFamily: "saans-regular" }}>
              {readingTimeText}
            </span>
          </>
        )}
      </div>
    </Link>
  );
}